import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Chip,
  Button,
  Divider,
  Card,
  CardContent,
  CardMedia,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search,
  Add,
  ViewInAr,
  Inventory2,
  DragIndicator,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useStorefront3DStore } from '../store/useStorefront3DStore';
import { useInventory } from '../../../../../contexts/InventoryContext';

const InventoryPanel = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const { 
    selectedItems, 
    addItem, 
    removeItem, 
    toggleItemVisibility 
  } = useStorefront3DStore();
  const { inventory, loading } = useInventory();

  const filteredInventory = inventory?.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = showOnlySelected ? selectedItems.some(selected => selected.id === item.id) : true;
    return matchesSearch && matchesFilter;
  }) || [];

  const handleAddToScene = (item) => {
    addItem({
      ...item,
      position: [Math.random() * 10 - 5, 0, Math.random() * 10 - 5],
      visible: true,
      scale: [1, 1, 1]
    });
  };

  const handleRemoveFromScene = (itemId) => {
    removeItem(itemId);
  };

  const isItemInScene = (itemId) => {
    return selectedItems.some(item => item.id === itemId);
  };

  const getItemInScene = (itemId) => {
    return selectedItems.find(item => item.id === itemId);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Inventory Items
        </Typography>
        
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search inventory..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Filter Controls */}
        <FormControlLabel
          control={
            <Switch
              checked={showOnlySelected}
              onChange={(e) => setShowOnlySelected(e.target.checked)}
              size="small"
            />
          }
          label="Show only items in scene"
          sx={{ mb: 1 }}
        />

        <Divider />
      </Box>

      {/* Items List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading inventory...
            </Typography>
          </Box>
        ) : filteredInventory.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Inventory2 sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'No items match your search' : 'No inventory items found'}
            </Typography>
          </Box>
        ) : (
          <List dense>
            {filteredInventory.map((item) => {
              const inScene = isItemInScene(item.id);
              const sceneItem = getItemInScene(item.id);
              
              return (
                <ListItem key={item.id} sx={{ px: 2 }}>
                  <ListItemAvatar>
                    <Avatar
                      src={item.images?.[0]?.url}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    >
                      <Inventory2 />
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" noWrap>
                          {item.name}
                        </Typography>
                        {inScene && (
                          <Chip 
                            label="In Scene" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          SKU: {item.sku} • Stock: {item.quantity}
                        </Typography>
                        {item.price && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            ${item.price}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {inScene && (
                        <IconButton
                          size="small"
                          onClick={() => toggleItemVisibility(item.id)}
                          title={sceneItem?.visible ? "Hide in scene" : "Show in scene"}
                        >
                          {sceneItem?.visible ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      )}
                      
                      <IconButton
                        size="small"
                        onClick={() => inScene ? handleRemoveFromScene(item.id) : handleAddToScene(item)}
                        color={inScene ? "error" : "primary"}
                        title={inScene ? "Remove from scene" : "Add to scene"}
                      >
                        {inScene ? <ViewInAr /> : <Add />}
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Footer Stats */}
      <Box sx={{ p: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          {selectedItems.length} items in scene • {filteredInventory.length} total items
        </Typography>
      </Box>
    </Box>
  );
};

export default InventoryPanel;
